import { useCallback, useEffect, useRef, useState } from 'react';
import { activateAuthenticatedCache, clearAuthenticatedCache } from './auth-query-cache';
import { queryClient } from './query-client';
import { broadcastLogout, subscribeToRemoteLogout } from './auth-session-events';
import { resetUnauthorizedLatch } from '@/shared/api/auth-fetch';
import { hydrateDecorScans } from '@/features/scene/lib/decor-scan';

/**
 * Session login gate (audit P0-1).
 *
 * The backend serves this SPA same-origin, so the signed session cookie set by
 * `POST /api/auth/login` is sent automatically with every subsequent `fetch`.
 * On boot we ask `/api/auth/me`:
 *  - `enabled: false`  → auth is off (dev) → render the app straight away.
 *  - `authenticated`   → render the app.
 *  - otherwise          → render the login screen.
 *
 * A global `holzcom-unauthorized` event (dispatched by the API client on any
 * 401) drops us back to the login screen when a session expires mid-use.
 */

type MeResponse = { enabled: boolean; authenticated: boolean; user: string | null };
type Status = 'checking' | 'authed' | 'login' | 'unavailable';

function isLoopbackHost(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1' || hostname === '[::1]';
}

function isTestAuthBypassEnabled(): boolean {
  return import.meta.env.VITE_HOLZCOM_TEST_AUTH_BYPASS === '1'
    && typeof window !== 'undefined'
    && isLoopbackHost(window.location.hostname);
}

async function activateLoopbackTestSession(): Promise<string> {
  const username = import.meta.env.VITE_HOLZCOM_TEST_AUTH_USER;
  const password = import.meta.env.VITE_HOLZCOM_TEST_AUTH_PASSWORD;
  if (!username || !password) return 'test-auth-bypass:loopback';

  const res = await fetch('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ username, password }),
  });
  if (!res.ok) throw new Error(`test auth login ${res.status}`);
  return `test-auth-bypass:${username}`;
}

async function fetchMe(): Promise<MeResponse> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error('auth/me timeout')), 10_000);
  });
  const res = await Promise.race([
    fetch('/api/auth/me', { credentials: 'same-origin' }),
    timeout,
  ]).finally(() => clearTimeout(timeoutId));
  if (!res.ok) throw new Error(`auth/me ${res.status}`);
  return res.json() as Promise<MeResponse>;
}

export function AuthGate({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<Status>('checking');
  const generationRef = useRef(0);
  const revocationRef = useRef<Promise<void> | null>(null);
  const invalidateGeneration = useCallback(() => { generationRef.current += 1; }, []);
  const revoke = useCallback(() => {
    ++generationRef.current;
    // Protected children disappear synchronously on the first signal. Cache
    // cleanup is idempotent and shared by any duplicate 401/logout signals.
    setStatus('login');
    if (!revocationRef.current) {
      revocationRef.current = clearAuthenticatedCache(queryClient).finally(() => {
        revocationRef.current = null;
      });
    }
    return revocationRef.current;
  }, []);
  const check = useCallback(async () => {
    const generation = ++generationRef.current;
    setStatus('checking');
    // Единая точка «сессия подтверждена»: реактивация кэша + гигиена latch +
    // отложенная гидрация декор-сканов. Последняя раньше звалась из main.tsx
    // на холодном старте — до логина, и авторизованный /api/materials/
    // decor-scans гарантированно давал 401 на экране входа (аудит 10.10).
    const markAuthed = async () => {
      resetUnauthorizedLatch();
      void hydrateDecorScans();
      setStatus('authed');
    };
    try {
      if (isTestAuthBypassEnabled()) {
        const cacheScope = await activateLoopbackTestSession();
        await activateAuthenticatedCache(queryClient, cacheScope);
        if (generation !== generationRef.current) return;
        await markAuthed();
        return;
      }
      const me = await fetchMe();
      if (!me.enabled || me.authenticated) {
        await activateAuthenticatedCache(queryClient, me.enabled ? `user:${me.user ?? 'unknown'}` : 'dev');
        if (generation !== generationRef.current) return;
        await markAuthed();
      } else {
        await clearAuthenticatedCache(queryClient);
        if (generation !== generationRef.current) return;
        setStatus('login');
      }
    } catch {
      await clearAuthenticatedCache(queryClient);
      if (generation !== generationRef.current) return;
      setStatus('unavailable');
    }
  }, []);

  useEffect(() => {
    void check();
    const onUnauthorized = () => {
      if (isTestAuthBypassEnabled()) return;
      void revoke();
    };
    const onLogout = () => {
      if (isTestAuthBypassEnabled()) return;
      void revoke();
      broadcastLogout();
      // Server cookie invalidation is best-effort and bounded. Local protected
      // UI/cache revocation above never waits for the network.
      void fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'same-origin',
        signal: AbortSignal.timeout(5_000),
      }).catch(() => undefined);
    };
    window.addEventListener('holzcom-unauthorized', onUnauthorized);
    window.addEventListener('holzcom-logout', onLogout);
    const unsubscribeRemoteLogout = subscribeToRemoteLogout(() => { void revoke(); });
    return () => {
      invalidateGeneration();
      window.removeEventListener('holzcom-unauthorized', onUnauthorized);
      window.removeEventListener('holzcom-logout', onLogout);
      unsubscribeRemoteLogout();
    };
  }, [check, invalidateGeneration, revoke]);

  if (status === 'checking') return null;
  if (status === 'unavailable') return <UnavailableScreen onRetry={() => void check()} />;
  if (status === 'login') return <LoginScreen onSuccess={() => void check()} />;
  return <>{children}</>;
}

function UnavailableScreen({ onRetry }: { onRetry: () => void }) {
  // The refusal state used to be a bare sentence on an unpainted page with an
  // unstyled browser button. It now shares the login card's frame so a failed
  // boot reads as a product screen rather than as a broken document.
  return (
    <div style={{ ...screenStyle, flexDirection: 'column', gap: 0 }}>
      <div role="alert" style={{ ...cardStyle, gap: 10, textAlign: 'left' }}>
        <div style={{ fontSize: 18, fontWeight: 600 }}>Сервис авторизации недоступен</div>
        <div style={{ fontSize: 13, color: 'rgb(var(--fg-muted))', lineHeight: 1.5 }}>
          Сервер не ответил на проверку сессии. Проверьте соединение и повторите попытку.
        </div>
        <button
          onClick={onRetry}
          style={{
            marginTop: 6,
            padding: '10px 14px',
            borderRadius: 8,
            border: 'none',
            fontWeight: 600,
            cursor: 'pointer',
            background: 'rgb(var(--accent, 37 99 235))',
            color: 'rgb(var(--accent-fg, 255 255 255))',
          }}
        >
          Повторить
        </button>
      </div>
    </div>
  );
}

const screenStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24,
  background: 'rgb(var(--bg))',
  color: 'rgb(var(--fg))',
};

const cardStyle: React.CSSProperties = {
  width: 320,
  maxWidth: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
  padding: 28,
  borderRadius: 12,
  background: 'rgb(var(--surface))',
  border: '1px solid rgb(var(--border))',
  boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
};

function LoginScreen({ onSuccess }: { onSuccess: () => void }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const disabled = busy || !username || !password;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ username, password }),
      });
      if (!res.ok) {
        setError(res.status === 401 ? 'Неверный логин или пароль' : `Ошибка входа (${res.status})`);
        return;
      }
      onSuccess();
    } catch {
      setError('Сервер недоступен');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={screenStyle}>
      <form onSubmit={submit} style={cardStyle}>
        <div style={{ fontSize: 18, fontWeight: 600 }}>HOLZCOM Viewer</div>
        <div style={{ fontSize: 13, color: 'rgb(var(--fg-muted))', marginTop: -8 }}>
          Вход в систему
        </div>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
          Логин
          <input
            autoFocus
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            style={inputStyle}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
          Пароль
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            style={inputStyle}
          />
        </label>
        {error && (
          <div role="alert" style={{ color: 'rgb(var(--err, 220 38 38))', fontSize: 13 }}>
            {error}
          </div>
        )}
        <button
          type="submit"
          disabled={disabled}
          style={{
            marginTop: 4,
            padding: '10px 14px',
            borderRadius: 8,
            border: 'none',
            fontWeight: 600,
            // A disabled control that still shows the hand cursor claims to be
            // clickable. Measured `cursor: pointer` while `disabled` was true.
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1,
            background: 'rgb(var(--accent, 37 99 235))',
            color: 'rgb(var(--accent-fg, 255 255 255))',
          }}
        >
          {busy ? 'Вход…' : 'Войти'}
        </button>
      </form>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  padding: '9px 11px',
  borderRadius: 8,
  border: '1px solid rgb(var(--border))',
  background: 'rgb(var(--bg))',
  color: 'rgb(var(--fg))',
  fontSize: 14,
  // `outline: none` here was inline, so it beat every stylesheet: a focused
  // login field had no outline, no box-shadow and an unchanged border. The
  // base :focus-visible ring in globals.css now owns the focus look.
};
